/**
 * Family & marriage cluster (L4) — /family/*
 *
 * Powers /family, /family/topic/$topic, /family/questions, /family/q/$slug.
 *
 * FACTUAL GUARDRAILS
 * - No service prices anywhere (SHOW_PUBLIC_PRICES=false). CTA = phone / LINE / email.
 * - Statutory references limited to provisions we can name:
 *   ประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 5 (ครอบครัว) — มาตรา 1448 (อายุ),
 *   มาตรา 1457 (สมรสสมบูรณ์เมื่อจดทะเบียน), มาตรา 1458 (ความยินยอม),
 *   มาตรา 1466 (สัญญาก่อนสมรสต้องจดแจ้งพร้อมการจดทะเบียนสมรส),
 *   มาตรา 1514 (การหย่าโดยความยินยอม/โดยคำพิพากษา);
 *   พ.ร.บ. จดทะเบียนครอบครัว พ.ศ. 2478 (ทะเบียนฐานะแห่งครอบครัว คร.22)
 * - "สมรสเท่าเทียม": พ.ร.บ. แก้ไขเพิ่มเติมประมวลกฎหมายแพ่งและพาณิชย์ (ฉบับที่ 24)
 *   พ.ศ. 2567 มีผลใช้บังคับ 23 มกราคม 2568 — บุคคลสองคนจดทะเบียนสมรสกันได้
 *   โดยไม่จำกัดเพศ
 * - แนวปฏิบัติที่ต่างกันรายสำนักทะเบียน/สถานทูต เขียนเป็น "ยืนยันกับหน่วยงานผู้รับ"
 * - ไม่รับประกันผลและไม่รับประกันระยะเวลา
 */

export const FAM_REVIEWED_TH = "8 ตุลาคม 2569";
export const FAM_REVIEWED_ISO = "2026-10-08T00:29:39.000Z";

export type FamTopic = {
  slug: string;
  th: string;
  en: string;
  authorityTh: string;
  channelsTh: string[];
  /** answer-first 50–80 คำ */
  summaryTh: string;
  factsTh: { label: string; value: string }[];
  requirementsTh: string[];
  stepsTh: { title: string; detail: string }[];
  tipsTh: string[];
  warningsTh: string[];
  citationTh: string;
  decisionTh: string;
};

const EQUALITY_NOTE =
  "ตั้งแต่วันที่ 23 มกราคม 2568 ประมวลกฎหมายแพ่งและพาณิชย์ซึ่งแก้ไขโดยพระราชบัญญัติแก้ไขเพิ่มเติมประมวลกฎหมายแพ่งและพาณิชย์ (ฉบับที่ 24) พ.ศ. 2567 เปิดให้บุคคลสองคนจดทะเบียนสมรสกันได้โดยไม่จำกัดเพศ";

export const FAM_TOPICS: FamTopic[] = [
  {
    slug: "marriage-registration-thailand",
    th: "จดทะเบียนสมรสในไทย (ไทย–ต่างชาติ)",
    en: "Marriage registration in Thailand",
    authorityTh: "สำนักงานเขต / ที่ว่าการอำเภอ (สำนักทะเบียน) กรมการปกครอง",
    channelsTh: [
      "สำนักงานเขตในกรุงเทพมหานคร",
      "ที่ว่าการอำเภอทั่วประเทศ",
      "การจดทะเบียนนอกสำนักทะเบียนตามที่นายทะเบียนอนุญาต",
    ],
    summaryTh:
      "การสมรสในไทยสมบูรณ์เมื่อจดทะเบียนตามประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1457 คู่ชาวต่างชาติต้องเตรียมหลักฐานสถานภาพการสมรสจากผู้ออกที่สำนักทะเบียนยอมรับ พร้อมคำแปลและการรับรองตามที่กำหนด แล้วจึงยื่นต่อสำนักทะเบียนพร้อมพยาน จำนวนวันทำการขึ้นกับคิวของสถานทูตและกรมการกงสุล",
    factsTh: [
      { label: "หน่วยงานผู้รับ", value: "สำนักทะเบียน (สำนักงานเขต/ที่ว่าการอำเภอ) กรมการปกครอง" },
      { label: "ผลทางกฎหมาย", value: "การสมรสสมบูรณ์เมื่อจดทะเบียน — ป.พ.พ. มาตรา 1457" },
      { label: "อายุขั้นต่ำ", value: "18 ปีบริบูรณ์ตาม ป.พ.พ. มาตรา 1448 เว้นแต่ศาลอนุญาตเมื่อมีเหตุอันสมควร" },
      { label: "ความยินยอม", value: "ต้องยินยอมโดยเปิดเผยต่อหน้านายทะเบียน — ป.พ.พ. มาตรา 1458" },
      { label: "สมรสเท่าเทียม", value: EQUALITY_NOTE },
      { label: "ค่าธรรมเนียม", value: "การจดทะเบียนในสำนักทะเบียนเป็นอัตราราชการ — ตรวจสอบกับสำนักทะเบียนที่จะยื่น" },
    ],
    requirementsTh: [
      "หนังสือเดินทางฉบับจริงของคู่สมรสชาวต่างชาติ พร้อมสำเนาหน้าข้อมูลและตราประทับขาเข้า",
      "บัตรประจำตัวประชาชนและทะเบียนบ้านของคู่สมรสฝ่ายไทย",
      "หลักฐานสถานภาพการสมรส เช่น Affirmation/Affidavit of Freedom to Marry จากสถานทูตหรือผู้ออกที่สำนักทะเบียนยอมรับ",
      "คำแปลภาษาไทยของหนังสือรับรอง ผ่านนิติกรณ์กรมการกงสุล กระทรวงการต่างประเทศ",
      "หลักฐานการสิ้นสุดการสมรสเดิม เช่น ใบสำคัญการหย่าหรือใบมรณบัตรคู่สมรสเดิม พร้อมคำแปลและการรับรอง",
      "พยานตามที่นายทะเบียนกำหนด พร้อมเอกสารแสดงตน",
      "ล่ามในกรณีคู่สมรสไม่เข้าใจภาษาไทย ตามที่สำนักทะเบียนกำหนด",
    ],
    stepsTh: [
      { title: "ขอหนังสือรับรองสถานภาพจากสถานทูต", detail: "คู่สมรสต่างชาติทำหนังสือรับรองว่ามีอิสระที่จะสมรสที่สถานทูตของตนในไทย รูปแบบและการนัดหมายต่างกันตามสถานทูต" },
      { title: "แปลเป็นภาษาไทยและรับรองนิติกรณ์", detail: "แปลเอกสารเป็นภาษาไทย แล้วยื่นรับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ ตามช่องทางที่เปิดให้บริการ" },
      { title: "ยื่นคำร้องต่อสำนักทะเบียน", detail: "นำเอกสารทั้งชุดพร้อมพยานไปยื่นที่สำนักงานเขตหรือที่ว่าการอำเภอ นายทะเบียนตรวจคุณสมบัติและความยินยอม" },
      { title: "จดทะเบียนและรับใบสำคัญการสมรส", detail: "ลงนามต่อหน้านายทะเบียนและพยาน รับใบสำคัญการสมรส (คร.3) และทะเบียนสมรส (คร.2)" },
      { title: "แปลและรับรองเพื่อใช้ต่างประเทศ", detail: "หากต้องใช้ในต่างประเทศ นำใบสำคัญการสมรสไปแปลและผ่านนิติกรณ์กรมการกงสุล แล้วรับรองสถานทูตปลายทางตามที่กำหนด" },
    ],
    tipsTh: [
      "ตรวจกับสถานทูตของคู่สมรสต่างชาติก่อนว่าออกหนังสือรับรองแบบใด และต้องนัดหมายล่วงหน้ากี่วัน",
      "สอบถามสำนักทะเบียนที่จะจดว่ารับคำแปลจากผู้ใด และต้องการพยานกี่คน",
      "หากต้องการใช้ทะเบียนสมรสยื่นวีซ่าในต่างประเทศ วางแผนขั้นตอนแปล-นิติกรณ์-รับรองสถานทูตไว้ตั้งแต่ต้น",
    ],
    warningsTh: [
      "การอยู่กินฉันสามีภริยาโดยไม่จดทะเบียนไม่ก่อให้เกิดการสมรสตามกฎหมายไทย",
      "หนังสือรับรองสถานภาพที่ไม่ผ่านนิติกรณ์กรมการกงสุล สำนักทะเบียนโดยทั่วไปไม่รับ",
    ],
    citationTh:
      "อ้างอิง: ประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1448, 1457, 1458 · พ.ร.บ. แก้ไขเพิ่มเติม ป.พ.พ. (ฉบับที่ 24) พ.ศ. 2567 · กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th) · กรมการปกครอง",
    decisionTh: "ก่อนนัดวันจด ให้เริ่มจากสำนักทะเบียนที่จะรับเรื่อง แล้วตรวจว่าประเทศของคู่สมรสออกหลักฐานสถานภาพผ่านสถานทูต หน่วยงานในประเทศต้นทาง หรือช่องทางใด เอกสารสถานภาพไม่ใช่เอกสารเดียวกับหนังสือเดินทาง และการแต่งงานไม่ทำให้ได้รับวีซ่าหรือสิทธิทำงานโดยอัตโนมัติ หากมีการสมรสเดิมต้องตรวจหลักฐานสิ้นสุดและการยอมรับเอกสารก่อนจองคิว",
  },
  {
    slug: "affirmation-freedom-to-marry",
    th: "หนังสือรับรองสถานะโสด / Affirmation of Freedom to Marry",
    en: "Affidavit / affirmation of freedom to marry",
    authorityTh: "สถานทูตหรือสถานกงสุลของผู้ยื่น + กรมการกงสุล กระทรวงการต่างประเทศ",
    channelsTh: [
      "สถานทูต/สถานกงสุลของประเทศผู้ยื่นในประเทศไทย",
      "กรมการกงสุล ถนนแจ้งวัฒนะ และสำนักงานสัญชาติและนิติกรณ์ที่เปิดให้บริการ",
      "สำนักทะเบียนสำหรับฝ่ายไทยที่ขอหนังสือรับรองความเป็นโสด",
    ],
    summaryTh:
      "หนังสือรับรองสถานะโสดคือเอกสารยืนยันว่าผู้ยื่นไม่มีคู่สมรสตามกฎหมาย ฝ่ายต่างชาติตรวจช่องทางจากสถานทูตหรือหน่วยงานประเทศต้นทางที่สำนักทะเบียนยอมรับ ส่วนฝ่ายไทยขอหนังสือรับรองความเป็นโสดจากสำนักทะเบียนตามหลักฐานทะเบียนราษฎร เอกสารทั้งสองแบบต้องแปลและผ่านการรับรองตามที่หน่วยงานปลายทางกำหนดก่อนใช้",
    factsTh: [
      { label: "ผู้ออกเอกสาร (ต่างชาติ)", value: "สถานทูต/สถานกงสุลของประเทศผู้ยื่นในประเทศไทย" },
      { label: "ผู้ออกเอกสาร (คนไทย)", value: "สำนักทะเบียนอำเภอ/เขต ตามหลักฐานทะเบียนราษฎร" },
      { label: "การรับรองต่อ", value: "นิติกรณ์กรมการกงสุลสำหรับใช้ในไทย หรือสถานทูตปลายทางสำหรับใช้ต่างประเทศ" },
      { label: "อายุเอกสาร", value: "หน่วยงานผู้รับกำหนดเอง หลายแห่งกำหนดเป็นเดือน — ยืนยันก่อนยื่น" },
      { label: "ภาษาที่ต้องใช้", value: "ใช้ในไทยต้องมีคำแปลภาษาไทย ใช้ต่างประเทศตามที่ปลายทางกำหนด" },
    ],
    requirementsTh: [
      "หนังสือเดินทางหรือบัตรประจำตัวประชาชนฉบับจริง",
      "หลักฐานการสิ้นสุดการสมรสเดิม (ถ้ามี) เช่น ใบสำคัญการหย่า คำพิพากษา หรือใบมรณบัตร",
      "ทะเบียนบ้านหรือหลักฐานที่อยู่ตามที่หน่วยงานผู้ออกกำหนด",
      "แบบคำร้องของสถานทูตหรือสำนักทะเบียน พร้อมการนัดหมายตามช่องทางที่เปิด",
      "คำแปลภาษาไทยหรือภาษาปลายทาง พร้อมการรับรองตามเส้นทางที่หน่วยงานผู้รับกำหนด",
    ],
    stepsTh: [
      { title: "ตรวจว่าใครเป็นผู้ออกเอกสาร", detail: "ฝ่ายต่างชาติขอที่สถานทูตของตน ฝ่ายไทยขอที่สำนักทะเบียน รูปแบบเอกสารต่างกันโดยสิ้นเชิง" },
      { title: "เตรียมหลักฐานสถานภาพ", detail: "รวบรวมหลักฐานการหย่าหรือการเสียชีวิตของคู่สมรสเดิม พร้อมคำแปลตามที่ผู้ออกเอกสารกำหนด" },
      { title: "ยื่นคำร้องและลงนามต่อหน้าเจ้าหน้าที่", detail: "หลายสถานทูตให้ผู้ยื่นสาบานหรือรับรองข้อความต่อหน้าเจ้าหน้าที่กงสุล ต้องไปด้วยตนเอง" },
      { title: "แปลและรับรองต่อ", detail: "นำเอกสารไปแปลภาษาไทยและยื่นนิติกรณ์กรมการกงสุลก่อนใช้ที่สำนักทะเบียน หรือรับรองสถานทูตปลายทางหากใช้ต่างประเทศ" },
    ],
    tipsTh: [
      "ถามสถานทูตล่วงหน้าว่ารับรองข้อความรูปแบบใด บางแห่งใช้คำว่า affirmation บางแห่งใช้ affidavit และเนื้อความไม่เหมือนกัน",
      "จัดคิวสถานทูตกับคิวนิติกรณ์กรมการกงสุลให้ต่อเนื่อง เพื่อไม่ให้เอกสารเกินอายุที่สำนักทะเบียนรับ",
      "หากเคยหย่ามาก่อน เตรียมเอกสารการหย่าฉบับสมบูรณ์ไปด้วยทุกครั้ง แม้สถานทูตยังไม่ได้ขอ",
    ],
    warningsTh: [
      "หนังสือรับรองที่ออกโดยไม่ได้ลงนามต่อหน้าเจ้าหน้าที่ตามระเบียบของสถานทูต อาจใช้ไม่ได้",
      "ต้องยืนยันเส้นทางการรับรองกับสำนักทะเบียน คำแปลอย่างเดียวอาจไม่เพียงพอสำหรับเอกสารต่างประเทศ",
    ],
    citationTh:
      "อ้างอิง: กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th) · กรมการปกครอง · ระเบียบของสถานทูตแต่ละประเทศ",
    decisionTh: "คำว่าโสดอาจไม่ครอบคลุมผู้ที่หย่าหรือคู่สมรสเสียชีวิตซึ่งมีสิทธิสมรสใหม่ จึงควรบอกประวัติสถานภาพตามจริงและขอเอกสารที่หน่วยงานผู้รับต้องการ หากสถานทูตไม่ออก affirmation ให้สอบถามทางเลือกที่สำนักทะเบียนยอมรับก่อนใช้หนังสือที่ร่างเอง อย่าถือว่าเอกสารจาก Notary ใช้แทนได้ทุกกรณี",
  },
  {
    slug: "marriage-abroad-recognition",
    th: "สมรสในต่างประเทศ + บันทึกฐานะแห่งครอบครัว (คร.22)",
    en: "Overseas marriage and Thai family-status registration",
    authorityTh: "สำนักทะเบียนอำเภอ/เขต ตาม พ.ร.บ. จดทะเบียนครอบครัว พ.ศ. 2478",
    channelsTh: [
      "สำนักงานเขต / ที่ว่าการอำเภอที่รับบันทึกฐานะแห่งครอบครัว",
      "สถานเอกอัครราชทูต/สถานกงสุลใหญ่ไทยในต่างประเทศสำหรับการรับรองเอกสารต้นทาง",
      "กรมการกงสุลสำหรับนิติกรณ์คำแปล",
    ],
    summaryTh:
      "คู่ที่จดทะเบียนสมรสตามกฎหมายต่างประเทศสามารถนำทะเบียนสมรสต่างประเทศมาบันทึกฐานะแห่งครอบครัว (คร.22) ที่สำนักทะเบียนในไทย เพื่อให้ทะเบียนราษฎรไทยแสดงสถานะสมรส ต้องรับรองเอกสารจากประเทศต้นทาง แปลเป็นภาษาไทย และผ่านนิติกรณ์กรมการกงสุลก่อนยื่น",
    factsTh: [
      { label: "แบบทะเบียน", value: "ทะเบียนฐานะแห่งครอบครัว (คร.22) ตาม พ.ร.บ. จดทะเบียนครอบครัว พ.ศ. 2478" },
      { label: "หน่วยงานผู้รับ", value: "สำนักทะเบียนอำเภอ/เขต" },
      { label: "เอกสารต้นทาง", value: "ทะเบียนสมรสต่างประเทศที่รับรองตามระเบียบของประเทศนั้นและของสถานทูตไทย" },
      { label: "คำแปล", value: "ต้องมีคำแปลภาษาไทยผ่านนิติกรณ์กรมการกงสุล" },
      { label: "ผลของการบันทึก", value: "เป็นการบันทึกสถานะในทะเบียนของไทย ไม่ใช่การจดทะเบียนสมรสใหม่" },
    ],
    requirementsTh: [
      "ทะเบียนสมรสหรือใบสำคัญการสมรสที่ออกโดยหน่วยงานต่างประเทศ ฉบับที่รับรองแล้ว",
      "คำแปลภาษาไทยผ่านนิติกรณ์กรมการกงสุล",
      "บัตรประจำตัวประชาชนและทะเบียนบ้านของฝ่ายไทย",
      "หนังสือเดินทางของคู่สมรสต่างชาติ พร้อมสำเนา",
      "หลักฐานการสิ้นสุดการสมรสเดิมของทั้งสองฝ่าย (ถ้ามี)",
    ],
    stepsTh: [
      { title: "รับรองเอกสารในประเทศต้นทาง", detail: "ดำเนินการรับรองทะเบียนสมรสตามระเบียบของประเทศที่ออกเอกสาร และผ่านการรับรองของสถานเอกอัครราชทูตไทยตามที่กำหนด" },
      { title: "แปลเป็นภาษาไทย", detail: "แปลเอกสารทั้งฉบับรวมตราประทับและข้อความรับรองทุกบรรทัด" },
      { title: "ยื่นนิติกรณ์กรมการกงสุล", detail: "ยื่นรับรองคำแปลที่กรมการกงสุลตามช่องทางที่เปิดให้บริการ" },
      { title: "ยื่นบันทึกฐานะแห่งครอบครัวที่สำนักทะเบียน", detail: "นำเอกสารทั้งชุดยื่นที่สำนักงานเขตหรือที่ว่าการอำเภอเพื่อบันทึก คร.22 และขอคัดสำเนาไว้ใช้งาน" },
    ],
    tipsTh: [
      "ขอคัดสำเนาทะเบียนสมรสต้นทางเผื่อไว้หลายฉบับ เพราะบางฉบับจะถูกเก็บไว้ที่หน่วยงานผู้รับ",
      "ตรวจว่าประเทศต้นทางเป็นภาคีอนุสัญญากรุงเฮกหรือไม่ เพราะเส้นทางการรับรองต่างกัน",
      "หากจะเปลี่ยนคำนำหน้าหรือนามสกุลตามการสมรส เตรียมเรื่องทะเบียนราษฎรต่อเนื่องในคราวเดียว",
    ],
    warningsTh: [
      "การบันทึกฐานะแห่งครอบครัวไม่ใช่การจดทะเบียนสมรสใหม่ อย่ายื่นซ้ำซ้อนเป็นการสมรสสองครั้ง",
      "คำแปลที่ตกตราประทับหรือข้อความรับรองท้ายเอกสาร มักถูกตีกลับให้แปลใหม่",
    ],
    citationTh:
      "อ้างอิง: พระราชบัญญัติจดทะเบียนครอบครัว พ.ศ. 2478 · กรมการปกครอง · กรมการกงสุล กระทรวงการต่างประเทศ",
    decisionTh: "แยกให้ชัดว่าเป็นการสมรสตามกฎหมายต่างประเทศหรือการจดทะเบียนต่อเจ้าหน้าที่ไทยในต่างประเทศ เพราะการนำมาใช้กับทะเบียนไทยอาจมีขั้นตอนต่างกัน คร.22 เป็นหลักฐานบันทึกฐานะแห่งครอบครัว ไม่ใช่หลักฐานรับรองผลทางกฎหมายทุกเรื่องในทุกประเทศ หากเป็นกรณีซับซ้อนควรตรวจความสมบูรณ์ของการสมรสและกฎหมายที่ใช้ก่อน",
  },
  {
    slug: "district-interpreter",
    th: "ล่ามจดทะเบียนสมรสที่อำเภอ/สำนักงานเขต",
    en: "District-office interpreter for marriage registration",
    authorityTh: "สำนักทะเบียนอำเภอ/เขต (นายทะเบียนเป็นผู้กำหนดเงื่อนไข)",
    channelsTh: ["นัดล่ามไปพร้อมคู่สมรสในวันจดทะเบียน", "ล่ามสำหรับขั้นตอนยื่นคำร้องล่วงหน้า"],
    summaryTh:
      "เมื่อคู่สมรสฝ่ายหนึ่งไม่เข้าใจภาษาไทย นายทะเบียนต้องมั่นใจว่าคู่สมรสยินยอมโดยเข้าใจข้อความที่ลงนามจริง ตามหลักความยินยอมใน ป.พ.พ. มาตรา 1458 สำนักทะเบียนหลายแห่งจึงกำหนดให้มีล่ามไปด้วย เงื่อนไขเรื่องคุณสมบัติล่ามและเอกสารแสดงตนต่างกันในแต่ละสำนักทะเบียน",
    factsTh: [
      { label: "เหตุผลที่ต้องมีล่าม", value: "ความยินยอมต้องเกิดจากความเข้าใจ — ป.พ.พ. มาตรา 1458" },
      { label: "ผู้กำหนดเงื่อนไข", value: "นายทะเบียนของสำนักทะเบียนที่จดทะเบียน" },
      { label: "เอกสารล่าม", value: "โดยทั่วไปแสดงบัตรประจำตัวประชาชนและลงนามในเอกสารตามที่นายทะเบียนกำหนด" },
      { label: "ภาษาที่ให้บริการ", value: "อังกฤษ จีน ญี่ปุ่น เกาหลี เยอรมัน ฝรั่งเศส รัสเซีย อาหรับ และภาษาอื่นตามคำขอ" },
    ],
    requirementsTh: [
      "แจ้งภาษา วันเวลา และสำนักทะเบียนล่วงหน้าเพื่อจัดล่ามให้ตรงคิว",
      "สำเนาเอกสารที่จะใช้ในวันจด เพื่อให้ล่ามเตรียมคำศัพท์เฉพาะ",
      "บัตรประจำตัวของล่ามตามที่นายทะเบียนกำหนด",
      "ข้อมูลชื่อคู่สมรสตามหนังสือเดินทางเพื่อความถูกต้องของการอ่านออกเสียงและการสะกด",
    ],
    stepsTh: [
      { title: "ยืนยันเงื่อนไขกับสำนักทะเบียน", detail: "สอบถามว่าสำนักทะเบียนกำหนดคุณสมบัติหรือเอกสารของล่ามอย่างไร และต้องมีล่ามในขั้นตอนใดบ้าง" },
      { title: "จองล่ามตามภาษาและวันเวลา", detail: "แจ้งภาษา วันเวลา และสถานที่ เพื่อจัดล่ามที่คุ้นเคยกับศัพท์ทะเบียนครอบครัว" },
      { title: "เตรียมเอกสารร่วมกันก่อนวันนัด", detail: "ล่ามอ่านเอกสารล่วงหน้าเพื่อเตรียมคำแปลของข้อความในทะเบียนสมรสและบันทึกข้อตกลง" },
      { title: "ปฏิบัติงานในวันจดทะเบียน", detail: "ล่ามแปลคำถามของนายทะเบียนและข้อความที่คู่สมรสจะลงนามให้ครบถ้วนก่อนลงนาม" },
    ],
    tipsTh: [
      "จองล่ามพร้อมกันกับการนัดหมายสำนักทะเบียน เพราะวันมงคลคิวเต็มเร็ว",
      "หากมีบันทึกข้อตกลงหรือสัญญาก่อนสมรส ให้ล่ามอ่านล่วงหน้าเพื่อแปลได้ครบถ้วน",
      "แจ้งสำเนียงหรือภาษาถิ่นของคู่สมรสไว้ล่วงหน้า เพื่อจัดล่ามให้ตรง",
    ],
    warningsTh: [
      "การใช้เพื่อนหรือญาติแปลโดยไม่ได้แจ้งล่วงหน้า บางสำนักทะเบียนไม่รับ",
      "หากคู่สมรสไม่เข้าใจข้อความที่ลงนาม อาจเกิดข้อโต้แย้งเรื่องความยินยอมภายหลัง",
    ],
    citationTh:
      "อ้างอิง: ประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1458 · แนวปฏิบัติของสำนักทะเบียน กรมการปกครอง",
    decisionTh: "เลือกภาษาที่คู่สมรสเข้าใจจริง ไม่ใช่เพียงภาษาที่อ่านหนังสือเดินทางได้ แจ้งสำนักทะเบียนว่าล่ามเป็นใครและต้องเตรียมเอกสารอะไร ล่ามช่วยสื่อสารแต่ไม่ใช่ผู้ให้ความยินยอมแทนคู่สมรส และไม่ควรตัดสินคำถามด้านผลทางกฎหมายของสัญญาแทนทนายความ",
  },
  {
    slug: "prenuptial-agreement",
    th: "สัญญาก่อนสมรส (Prenuptial Agreement)",
    en: "Prenuptial agreement in Thailand",
    authorityTh: "สำนักทะเบียนที่จดทะเบียนสมรส (จดแจ้งพร้อมการสมรส)",
    channelsTh: ["จดแจ้งข้อความในทะเบียนสมรสขณะจดทะเบียน", "แนบเอกสารสัญญาท้ายทะเบียนสมรสตามที่นายทะเบียนกำหนด"],
    summaryTh:
      "สัญญาก่อนสมรสตามประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1466 จะมีผลต่อเมื่อจดแจ้งข้อความไว้ในทะเบียนสมรสพร้อมกับการจดทะเบียนสมรส หรือทำเป็นหนังสือลงลายมือชื่อคู่สมรสและพยานสองคนแนบท้ายทะเบียนสมรสและจดแจ้งไว้ ทำหลังสมรสแล้วไม่มีผลเป็นสัญญาก่อนสมรส",
    factsTh: [
      { label: "ฐานกฎหมาย", value: "ประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1466" },
      { label: "จังหวะเวลา", value: "ต้องดำเนินการพร้อมการจดทะเบียนสมรส ไม่ใช่ภายหลัง" },
      { label: "รูปแบบ", value: "จดแจ้งในทะเบียนสมรส หรือทำหนังสือแนบท้ายพร้อมพยานสองคนและจดแจ้งไว้" },
      { label: "ขอบเขต", value: "ใช้กำหนดเรื่องทรัพย์สินระหว่างสามีภริยา ไม่ใช่การตกลงเรื่องอำนาจปกครองบุตรล่วงหน้า" },
      { label: "ภาษา", value: "คู่สมรสต่างชาติควรมีฉบับสองภาษาและล่ามในวันลงนาม" },
    ],
    requirementsTh: [
      "ร่างสัญญาที่ระบุทรัพย์สินและข้อตกลงชัดเจน พร้อมฉบับแปลหากฝ่ายหนึ่งไม่อ่านภาษาไทย",
      "บัตรประจำตัวประชาชน/หนังสือเดินทางของคู่สมรสทั้งสองฝ่าย",
      "พยานสองคนพร้อมเอกสารแสดงตน",
      "รายการทรัพย์สินและหลักฐานความเป็นเจ้าของที่จะระบุในสัญญา",
      "การนัดหมายกับสำนักทะเบียนให้ตรงกับวันจดทะเบียนสมรส",
    ],
    stepsTh: [
      { title: "หารือขอบเขตและร่างสัญญา", detail: "กำหนดว่าทรัพย์สินใดเป็นสินส่วนตัว การจัดการทรัพย์สินระหว่างสมรส และการจัดการเมื่อการสมรสสิ้นสุด" },
      { title: "จัดทำฉบับสองภาษา", detail: "แปลให้ตรงกันทั้งฉบับ เพื่อให้คู่สมรสทั้งสองเข้าใจข้อความเดียวกันก่อนลงนาม" },
      { title: "ประสานสำนักทะเบียนล่วงหน้า", detail: "แจ้งนายทะเบียนว่าจะจดแจ้งสัญญาก่อนสมรสพร้อมการจดทะเบียน เพื่อเตรียมแบบและขั้นตอน" },
      { title: "ลงนามและจดแจ้งในวันจดทะเบียนสมรส", detail: "ลงนามพร้อมพยานสองคนต่อหน้านายทะเบียน และจดแจ้งข้อความไว้ในทะเบียนสมรส" },
    ],
    tipsTh: [
      "เริ่มร่างล่วงหน้าอย่างน้อยหลายสัปดาห์ เพราะต้องแปลและตรวจสองภาษาก่อนวันจด",
      "ระบุทรัพย์สินให้เจาะจง เช่น เลขที่โฉนด เลขบัญชี หรือทะเบียนหุ้น แทนคำกว้าง ๆ",
      "จัดล่ามในวันลงนามหากฝ่ายหนึ่งไม่เข้าใจภาษาไทย เพื่อลดข้อโต้แย้งเรื่องความเข้าใจ",
    ],
    warningsTh: [
      "ทำสัญญาหลังจดทะเบียนสมรสแล้ว ไม่มีผลเป็นสัญญาก่อนสมรสตามมาตรา 1466",
      "ข้อความที่ขัดต่อความสงบเรียบร้อยหรือศีลธรรมอันดีของประชาชนอาจตกเป็นโมฆะ",
      "การไม่จดแจ้งไว้ในทะเบียนสมรสทำให้สัญญาไม่มีผลตามที่ตั้งใจ แม้ลงนามครบถ้วน",
    ],
    citationTh: "อ้างอิง: ประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1465–1466 · กรมการปกครอง",
    decisionTh: "ก่อนร่างให้แยกทรัพย์สิน หนี้ ผู้ถือกรรมสิทธิ์ และหลักฐานวันที่ได้มา พร้อมระบุสิ่งที่ต้องการจัดการเมื่อสมรส อย่าใช้ข้อความสละสิทธิทุกอย่างแทนการวิเคราะห์ข้อจำกัดตามกฎหมาย หากมีทรัพย์หรือคู่สมรสในต่างประเทศให้ขอคำแนะนำกฎหมายประเทศนั้นแยกจากการจดแจ้งสัญญาในไทย",
  },
  {
    slug: "divorce-registration",
    th: "จดทะเบียนหย่า (หย่าโดยความยินยอม / โดยคำพิพากษา)",
    en: "Divorce registration in Thailand",
    authorityTh: "สำนักทะเบียนอำเภอ/เขต หรือศาลเยาวชนและครอบครัว",
    channelsTh: [
      "สำนักทะเบียนสำหรับการหย่าโดยความยินยอม",
      "ศาลเยาวชนและครอบครัวสำหรับการหย่าโดยคำพิพากษา",
      "สถานเอกอัครราชทูต/สถานกงสุลใหญ่ไทยในต่างประเทศตามที่เปิดให้บริการ",
    ],
    summaryTh:
      "ตามประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1514 การหย่าทำได้สองทาง คือหย่าโดยความยินยอมซึ่งต้องทำเป็นหนังสือมีพยานลงลายมือชื่ออย่างน้อยสองคนและจดทะเบียนหย่าที่สำนักทะเบียน กับหย่าโดยคำพิพากษาของศาลเมื่อมีเหตุฟ้องหย่าตามกฎหมาย ระยะเวลาต่างกันมากระหว่างสองทาง",
    factsTh: [
      { label: "ฐานกฎหมาย", value: "ประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1514 และมาตราว่าด้วยเหตุฟ้องหย่า" },
      { label: "หย่าโดยความยินยอม", value: "ทำเป็นหนังสือ มีพยานลงลายมือชื่ออย่างน้อยสองคน และจดทะเบียนหย่า" },
      { label: "หย่าโดยคำพิพากษา", value: "ยื่นฟ้องต่อศาลเยาวชนและครอบครัวเมื่อมีเหตุตามกฎหมาย" },
      { label: "บุตรและทรัพย์สิน", value: "ควรทำบันทึกข้อตกลงเรื่องอำนาจปกครองบุตร ค่าอุปการะเลี้ยงดู และทรัพย์สินให้ชัดเจน" },
      { label: "การใช้ในต่างประเทศ", value: "ใบสำคัญการหย่าต้องแปลและผ่านนิติกรณ์ก่อนใช้ต่างประเทศ" },
    ],
    requirementsTh: [
      "ใบสำคัญการสมรสฉบับจริง",
      "บัตรประจำตัวประชาชน/หนังสือเดินทางของทั้งสองฝ่าย และทะเบียนบ้านของฝ่ายไทย",
      "หนังสือหย่าที่ลงลายมือชื่อคู่หย่าและพยานอย่างน้อยสองคน",
      "บันทึกข้อตกลงเรื่องบุตรและทรัพย์สิน (ถ้ามี)",
      "ล่ามในกรณีฝ่ายหนึ่งไม่เข้าใจภาษาไทย ตามที่สำนักทะเบียนกำหนด",
    ],
    stepsTh: [
      { title: "เลือกช่องทางที่ตรงกับข้อเท็จจริง", detail: "ตกลงกันได้ใช้ช่องทางหย่าโดยความยินยอมที่สำนักทะเบียน ตกลงกันไม่ได้ต้องใช้ช่องทางศาล" },
      { title: "จัดทำหนังสือหย่าและบันทึกข้อตกลง", detail: "ระบุเรื่องอำนาจปกครองบุตร ค่าอุปการะเลี้ยงดู และการแบ่งทรัพย์สินให้ครบถ้วนก่อนไปสำนักทะเบียน" },
      { title: "จดทะเบียนหย่าที่สำนักทะเบียน", detail: "กรณีสำนักทะเบียนเดียวกัน ทั้งสองฝ่ายไปตามนัดพร้อมพยาน หากอยู่ต่างพื้นที่หรือต่างประเทศให้ตรวจช่องทางหย่าต่างสำนักทะเบียนกับทั้งสองหน่วยงานก่อน นายทะเบียนตรวจเงื่อนไขและออกเอกสารตามขั้นตอนที่ใช้" },
      { title: "ดำเนินการต่อเนื่องหลังหย่า", detail: "ปรับปรุงทะเบียนราษฎร คำนำหน้า นามสกุล และแจ้งหน่วยงานที่เกี่ยวข้อง รวมถึงแปลเอกสารหากต้องใช้ต่างประเทศ" },
    ],
    tipsTh: [
      "ทำบันทึกข้อตกลงเรื่องบุตรให้ละเอียดตั้งแต่ต้น เพื่อลดข้อพิพาทในภายหลัง",
      "ตรวจว่าประเทศของคู่สมรสต่างชาติรับรองการหย่าที่จดในไทยอย่างไร ก่อนวางแผนสมรสใหม่",
      "เตรียมใบสำคัญการสมรสฉบับจริง เพราะสำนักทะเบียนต้องเรียกคืนหรือบันทึกรายการ",
    ],
    warningsTh: [
      "การเซ็นหนังสือหย่าไว้เฉย ๆ โดยไม่จดทะเบียนหย่า ไม่ทำให้การสมรสสิ้นสุด",
      "การหย่าที่ไม่ระบุเรื่องบุตรไว้ อาจต้องกลับมาฟ้องร้องกันภายหลัง",
    ],
    citationTh: "อ้างอิง: ประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1514 และบรรพ 5 · กรมการปกครอง · ศาลเยาวชนและครอบครัวกลาง",
    decisionTh: "แยกกรณีทั้งสองฝ่ายยินยอมกับกรณีมีข้อพิพาทก่อนเตรียมเอกสาร การเซ็นข้อตกลงอย่างเดียวไม่แทนการจดทะเบียนหย่า ในเรื่องบุตรควรระบุผู้ใช้อำนาจปกครอง ค่าเลี้ยงดูและการติดต่ออย่างชัดเจนโดยคำนึงถึงประโยชน์ของบุตร หากมีคำพิพากษาต้องตรวจสถานะคดีและเอกสารที่นายทะเบียนต้องการ ไม่ใช้ขั้นตอนหย่าโดยยินยอมแทนทุกกรณี",
  },
  {
    slug: "marriage-documents-overseas-use",
    th: "นำทะเบียนสมรสไทยไปใช้ต่างประเทศ",
    en: "Using Thai marriage certificates abroad",
    authorityTh: "กรมการกงสุล กระทรวงการต่างประเทศ + สถานทูตประเทศปลายทาง",
    channelsTh: ["นิติกรณ์กรมการกงสุล", "การรับรองของสถานทูตประเทศปลายทางในไทย"],
    summaryTh:
      "ทะเบียนสมรสหรือใบสำคัญการสมรสที่ออกในไทย หากจะใช้ยื่นวีซ่าคู่สมรส สวัสดิการ หรือทะเบียนราษฎรในต่างประเทศ ต้องตรวจชนิดเอกสาร ภาษา และเส้นทางการรับรองกับหน่วยงานผู้รับก่อน อาจต้องแปล นิติกรณ์กรมการกงสุล และรับรองสถานทูตตามข้อกำหนดของปลายทาง ไม่ใช่ทุกประเทศหรือทุกการใช้งานต้องดำเนินการเหมือนกัน",
    factsTh: [
      { label: "ลำดับที่ถูกต้อง", value: "คัดสำเนาจากสำนักทะเบียน → แปล → นิติกรณ์กรมการกงสุล → สถานทูตปลายทาง" },
      { label: "ค่าธรรมเนียมนิติกรณ์", value: "ตรวจอัตราราชการและประเภทบริการที่เปิดกับกรมการกงสุลก่อนยื่น แยกจากค่าคำแปลและค่าดำเนินการ" },
      { label: "อายุเอกสาร", value: "ปลายทางหลายแห่งรับสำเนาที่คัดใหม่ภายในระยะเวลาที่กำหนด — ยืนยันก่อนเริ่ม" },
      { label: "Apostille", value: "ประเทศไทยเข้าเป็นภาคีอนุสัญญากรุงเฮกว่าด้วยการยกเลิกการรับรองเอกสารมหาชนต่างประเทศ โดยจะมีผลใช้บังคับกับไทยวันที่ 28 กุมภาพันธ์ 2570 (28 Feb 2027)" },
    ],
    requirementsTh: [
      "สำเนาทะเบียนสมรส (คร.2) หรือใบสำคัญการสมรส (คร.3) ที่คัดจากสำนักทะเบียน",
      "คำแปลภาษาปลายทางที่ครบทุกบรรทัดรวมตราประทับ",
      "หนังสือเดินทางของคู่สมรสสำหรับการยื่นสถานทูตบางแห่ง",
      "แบบฟอร์มและการนัดหมายของสถานทูตปลายทาง",
    ],
    stepsTh: [
      { title: "คัดสำเนาฉบับใหม่", detail: "ขอคัดสำเนาทะเบียนสมรสจากสำนักทะเบียนให้เป็นฉบับใหม่ เพื่อไม่ให้เกินอายุที่ปลายทางรับ" },
      { title: "แปลตามภาษาที่ปลายทางกำหนด", detail: "แปลครบทุกบรรทัด รวมหัวกระดาษ ตราประทับ และลายมือชื่อ" },
      { title: "ยื่นนิติกรณ์กรมการกงสุล", detail: "เลือกแบบธรรมดาหรือแบบด่วนตามกำหนดเวลาที่ต้องใช้ ค่าธรรมเนียมเป็นอัตราราชการที่ประกาศไว้" },
      { title: "รับรองที่สถานทูตปลายทาง", detail: "ยื่นตามระเบียบและคิวของสถานทูตประเทศปลายทางในไทย แล้วตรวจความถูกต้องก่อนส่งออกนอกประเทศ" },
    ],
    tipsTh: [
      "ถามหน่วยงานปลายทางก่อนว่าต้องการฉบับภาษาอังกฤษหรือภาษาราชการของประเทศนั้น",
      "ทำหลายชุดในคราวเดียวถ้าต้องยื่นหลายหน่วยงาน จะประหยัดรอบการเดินเรื่อง",
      "ตรวจการสะกดชื่อบนคำแปลให้ตรงกับหนังสือเดินทางทุกตัวอักษร",
    ],
    warningsTh: [
      "หากปลายทางกำหนดลำดับการรับรอง ต้องดำเนินการตามลำดับนั้นและตรวจเอกสารที่สถานทูตรับก่อนนัดหมาย",
      "สำเนาเก่าที่คัดไว้นานอาจเกินอายุที่ปลายทางรับ ต้องคัดใหม่",
      "อย่าอ้างว่าเอกสารไทยใช้ Apostille ได้ในขณะนี้ เพราะจะมีผลใช้บังคับกับไทยวันที่ 28 กุมภาพันธ์ 2570",
    ],
    citationTh:
      "อ้างอิง: กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th) · HCCH — สถานะภาคีอนุสัญญา Apostille ของประเทศไทย มีผล 28 กุมภาพันธ์ 2570",
    decisionTh: "เริ่มจากถามหน่วยงานผู้รับว่าต้องใช้ คร.2 หรือ คร.3 ฉบับคัดรับรองหรือฉบับจริง คำแปลภาษาใด และต้องมีการรับรองระดับใด การรับรองลายมือชื่อหรือตราประทับไม่ใช่การรับประกันว่าเอกสารถูกยอมรับสำหรับวีซ่า สวัสดิการ หรือสิทธิครอบครัวทุกประเภท เก็บคำตอบหน่วยงานผู้รับไว้ก่อนเริ่มแปล",
  },
  {
    slug: "name-title-change-after-marriage",
    th: "เปลี่ยนคำนำหน้า/นามสกุลหลังสมรสหรือหย่า",
    en: "Title and surname change after marriage or divorce",
    authorityTh: "สำนักทะเบียนอำเภอ/เขต (งานทะเบียนราษฎรและชื่อบุคคล)",
    channelsTh: ["สำนักงานเขต / ที่ว่าการอำเภอ", "งานบัตรประจำตัวประชาชนสำหรับการทำบัตรใหม่"],
    summaryTh:
      "หลังจดทะเบียนสมรสหรือหย่า คู่สมรสฝ่ายไทยสามารถเลือกใช้คำนำหน้าและนามสกุลตามสิทธิที่กฎหมายกำหนด แล้วปรับปรุงบัตรประจำตัวประชาชน ทะเบียนบ้าน หนังสือเดินทาง และเอกสารกับหน่วยงานอื่นให้ตรงกัน ควรทำต่อเนื่องในคราวเดียวเพื่อไม่ให้ข้อมูลขัดกันระหว่างหน่วยงาน",
    factsTh: [
      { label: "หน่วยงานผู้รับ", value: "สำนักทะเบียนอำเภอ/เขต" },
      { label: "เอกสารหลัก", value: "ใบสำคัญการสมรส (คร.3) หรือใบสำคัญการหย่า (คร.7)" },
      { label: "ผลต่อเอกสารอื่น", value: "ต้องปรับปรุงบัตรประชาชน ทะเบียนบ้าน หนังสือเดินทาง บัญชีธนาคาร และเอกสารสิทธิ" },
      { label: "ทางเลือก", value: "การใช้นามสกุลคู่สมรสเป็นสิทธิที่เลือกได้ ไม่ใช่หน้าที่บังคับ" },
    ],
    requirementsTh: [
      "ใบสำคัญการสมรสหรือใบสำคัญการหย่าฉบับจริง",
      "บัตรประจำตัวประชาชนและทะเบียนบ้าน",
      "แบบคำร้องของสำนักทะเบียน",
      "เอกสารของคู่สมรสต่างชาติพร้อมคำแปลตามที่นายทะเบียนกำหนด",
    ],
    stepsTh: [
      { title: "ยื่นคำร้องที่สำนักทะเบียน", detail: "แจ้งความประสงค์เรื่องคำนำหน้าและนามสกุล พร้อมแสดงใบสำคัญการสมรสหรือการหย่า" },
      { title: "แก้ไขรายการทะเบียนราษฎร", detail: "นายทะเบียนบันทึกการเปลี่ยนแปลงในฐานข้อมูลทะเบียนราษฎรและทะเบียนบ้าน" },
      { title: "ทำบัตรประจำตัวประชาชนใหม่", detail: "ยื่นทำบัตรใหม่ให้ข้อมูลตรงกับทะเบียนราษฎรที่แก้ไขแล้ว" },
      { title: "ปรับปรุงเอกสารกับหน่วยงานอื่น", detail: "แจ้งหนังสือเดินทาง ธนาคาร ประกัน กรมที่ดิน และนายจ้าง เพื่อไม่ให้ชื่อขัดกันระหว่างระบบ" },
    ],
    tipsTh: [
      "ทำรายการเอกสารที่มีชื่อเราอยู่ทั้งหมดก่อน แล้วไล่แก้ทีละหน่วยงานหลังได้บัตรใหม่",
      "หากมีทรัพย์สินหรือสัญญาที่ใช้ชื่อเดิม ให้เก็บสำเนาใบสำคัญไว้แสดงความต่อเนื่องของบุคคล",
      "ผู้ที่ต้องยื่นวีซ่าเร็ว ๆ นี้ ควรวางลำดับการเปลี่ยนชื่อกับกำหนดยื่นวีซ่าให้ดี",
    ],
    warningsTh: [
      "ชื่อในหนังสือเดินทางกับตั๋วเดินทางหรือวีซ่าที่ไม่ตรงกัน ทำให้เดินทางมีปัญหา",
      "เปลี่ยนชื่อระหว่างที่คำขอวีซ่ากำลังพิจารณา อาจต้องยื่นเอกสารเพิ่มทั้งชุด",
    ],
    citationTh: "อ้างอิง: กรมการปกครอง งานทะเบียนราษฎรและทะเบียนชื่อบุคคล · ประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 5",
    decisionTh: "การสมรสไม่บังคับให้ทุกคนเปลี่ยนนามสกุล และสิทธิเรื่องคำนำหน้าต้องพิจารณาตามกฎหมายเฉพาะ หากมีการเดินทางหรือคำขอวีซ่าอยู่ระหว่างพิจารณา ให้ตรวจลำดับกับหน่วยงานก่อนเปลี่ยนเอกสาร เก็บหลักฐานเชื่อมชื่อเดิมกับชื่อใหม่ไว้ใช้กับสัญญา ทรัพย์สิน และเอกสารต่างประเทศ",
  },
];

export const FAM_TOPIC_BY_SLUG: Record<string, FamTopic> = Object.fromEntries(
  FAM_TOPICS.map((t) => [t.slug, t]),
);
